import React from 'react';
import * as PropTypes from 'prop-types';
import cname from 'classnames';
import {ErrorMessage, Field} from 'formik';

export const FieldRow = rowProps => {
    const {
        component,
        fieldName,
        fieldProps,
        fieldType,
        forceInvalidState,
        helpText,
        label,
        note,
        optional,
        sizes,
        ...formikProps
    } = rowProps;

    const {
        errors,
        isSubmitting,
        handleBlur,
        setFieldTouched,
        touched,
    } = formikProps;

    const props = {
        ...fieldProps,
        className: cname({
            'form-control': component === null,
            'is-invalid': (errors[fieldName] && touched[fieldName]) || forceInvalidState,
        }),
        disabled: isSubmitting,
        name: fieldName,
        onBlur: (...args) => {
            setFieldTouched(fieldName);
            if (typeof fieldProps.onBlur !== 'undefined') {
                fieldProps.onBlur(...args);
            }

            if (typeof args[0]?.target !== 'undefined') {
                handleBlur(...args);
            }
        },
    };

    if (component === null) {
        props.type = fieldType;
    }
    else {
        props.component = component;
    }

    let helpInfo = null;
    if (helpText !== '') {
        props['aria-describedby'] = `${fieldName}-help-text`;
        helpInfo = (
            <small id={`${fieldName}-help-text`} className="form-text text-muted">
                {helpText}
            </small>
        );
    }

    if (label === '') {
        props['aria-label'] = `${fieldName}`;
    }
    else {
        props['aria-labelledby'] = `${fieldName}-label`;
    }

    const labelClass = `col-6 col-sm-${sizes.label} col-form-label`;
    const fieldClass = cname({
        [`col-6 col-sm-${sizes.field}`]: true,
        [`offset-6 offset-sm-${sizes.label}`]: label === '',
    });

    return (
        <div className="form-group row mb-3">
            {label !== '' && (
                <label className={labelClass} id={`${fieldName}-label`}>
                    {label}
                    {optional && (
                        <React.Fragment>
                            {' '}
                            <em>(Optional)</em>
                        </React.Fragment>
                    )}
                    {note && (
                        <React.Fragment>
                            {' '}
                            <em>
                                (
                                {note}
                                )
                            </em>
                        </React.Fragment>
                    )}
                </label>
            )}
            <div className={fieldClass}>
                <Field {...props} />
                {helpInfo}
                <ErrorMessage name={fieldName} className="invalid-feedback" component="div" />
            </div>
        </div>
    );
};

FieldRow.defaultProps = {
    component: null,
    fieldProps: {},
    fieldType: 'text',
    forceInvalidState: false,
    helpText: '',
    label: '',
    optional: false,
    sizes: {
        field: 4,
        label: 4,
    },
};

FieldRow.propTypes = {
    component: PropTypes.any,
    fieldName: PropTypes.string.isRequired,
    fieldProps: PropTypes.object,
    fieldType: PropTypes.string,
    forceInvalidState: PropTypes.bool,
    helpText: PropTypes.string,
    label: PropTypes.string,
    optional: PropTypes.bool,
    sizes: PropTypes.shape({
        field: PropTypes.number,
        label: PropTypes.number,
    }),
};

export default FieldRow;
